import React, { useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../lib/api';
import './RealizedGains.css';

function WithdrawalForm({ withdrawal = null, onSuccess, onCancel }) {
  const isEdit = withdrawal !== null;

  const [formData, setFormData] = useState({
    amount: withdrawal?.amount || '',
    withdrawalDate: withdrawal?.date || new Date().toISOString().split('T')[0],
    currency: withdrawal?.currency || 'USD',
    platform: withdrawal?.platform || '',
    notes: withdrawal?.notes || '',
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const CURRENCIES = ['USD', 'EUR', 'GBP', 'SEK', 'NOK', 'DKK'];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Validation
      if (!formData.amount || !formData.withdrawalDate) {
        throw new Error('Please fill in all required fields');
      }

      // Validate amount
      if (isNaN(parseFloat(formData.amount)) || parseFloat(formData.amount) <= 0) {
        throw new Error('Amount must be a positive number');
      }

      // Validate date
      const withdrawalDate = new Date(formData.withdrawalDate);
      if (isNaN(withdrawalDate.getTime())) {
        throw new Error('Invalid withdrawal date');
      }

      const payload = {
        ...formData,
        amount: parseFloat(formData.amount),
      };

      if (isEdit) {
        await axios.put(
          `${API_BASE_URL}/transactions/withdrawals/${withdrawal.transactionId}`,
          payload,
          { withCredentials: true }
        );
      } else {
        await axios.post(
          `${API_BASE_URL}/transactions/withdrawals`,
          payload,
          { withCredentials: true }
        );
      }

      onSuccess();
    } catch (err) {
      console.error('Error saving withdrawal:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save withdrawal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content withdrawal-form">
        <div className="modal-header">
          <h2>{isEdit ? 'Edit' : 'Add'} Withdrawal</h2>
          <button onClick={onCancel} className="btn-close" disabled={loading}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-message">{error}</div>}

          <div className="form-grid">
            <div className="form-row">
              <label htmlFor="amount">Amount *</label>
              <input
                type="number"
                id="amount"
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                step="0.01"
                min="0.01"
                placeholder="e.g., 5000.00"
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <label htmlFor="currency">Currency *</label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleChange}
                required
                disabled={loading}
              >
                {CURRENCIES.map(curr => (
                  <option key={curr} value={curr}>{curr}</option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <label htmlFor="withdrawalDate">Withdrawal Date *</label>
              <input
                type="date"
                id="withdrawalDate"
                name="withdrawalDate"
                value={formData.withdrawalDate}
                onChange={handleChange}
                max={new Date().toISOString().split('T')[0]}
                required
                disabled={loading}
              />
            </div>

            <div className="form-row">
              <label htmlFor="platform">Platform/Broker</label>
              <input
                type="text"
                id="platform"
                name="platform"
                value={formData.platform}
                onChange={handleChange}
                placeholder="e.g., Interactive Brokers"
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-row full-width">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              placeholder="Optional notes about this withdrawal..."
              rows="3"
              disabled={loading}
            />
          </div>

          <div className="form-actions">
            <button type="button" onClick={onCancel} className="btn-cancel" disabled={loading}>
              Cancel
            </button>
            <button type="submit" className="btn-submit" disabled={loading}>
              {loading ? 'Saving...' : (isEdit ? 'Update' : 'Add')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WithdrawalForm;
